'use client';
import React, { useState, useEffect } from 'react';
import { Link, usePathname } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import GetInTouch from '@/components/modals/get-in-touch';
import { cn } from '@/lib/utils';
import { LayoutGroup, motion } from 'motion/react';
import MobileMenu from './mobile-menu';
import LanguageSwitcher from './language-switcher';
import { Button } from '../ui/button';
import Icons from '@/lib/icons';

const Header = () => {
    const pathname = usePathname();
    const t = useTranslations('nav');
    const tA11y = useTranslations('accessibility');
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isGetInTouchOpen, setIsGetInTouchOpen] = useState(false);
    const [isNearTop, setIsNearTop] = useState(true);

    const navigationItems = [
        { href: '/about-us' as const, label: t('aboutUs') },
        { href: '/services' as const, label: t('services') },
        { href: '/integrations' as const, label: t('integrations') },
    ];

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const handleCloseMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    // Close menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [pathname]);

    // Scroll detection for navigation animation
    useEffect(() => {
        const handleScroll = () => {
            const scrollY = window.scrollY;
            setIsNearTop(scrollY <= 250);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            <header
                className="site-header absolute z-10 pt-[15px] lg:pt-[30px] top-0 left-0 right-0 w-full"
            >
                <div className='container mx-auto h-full'>
                    <div className='flex items-center justify-between h-full'>
                        {/* Left - Logo */}
                        <div className='flex items-center'>
                            <Link href='/' className='block w-[40px] md:w-[48px] lg:w-[80px]'>
                                <img src="/assets/brand/logo.svg" alt="Flyingcode" width={80} height={80} className='w-auto h-auto max-w-full' />
                            </Link>
                        </div>

                        {/* Middle - Navigation (Desktop) */}
                        <nav aria-label={tA11y('mainNavigation')} className='hidden md:flex items-center fixed start-2/4 -translate-x-2/4 bg-[linear-gradient(180deg,_rgba(18,_17,_36,_0.5)_0%,_#121124_100%)] p-[3px_4px] lg:p-[5px_6px] rounded-[16px] lg:rounded-[20px]'>
                            <LayoutGroup>
                            <ul className='flex items-center gap-[4px] lg:gap-2'>
                                 <li className='relative'>
                                    <Link
                                        href='/'
                                        aria-label={t('home')}
                                        className={cn(
                                            'relative outline-none focus-visible:ring-2 focus-visible:ring-theme/50 focus-visible:ring-offset-0 inline-flex items-center justify-center rounded-[13px] lg:rounded-[15px] transition-all duration-300',
                                            pathname !== '/' && 'hover:text-white/50',
                                            isNearTop
                                                ? 'text-white px-[11px] lg:px-[25px] py-[8px] lg:py-4 min-h-[40px] lg:min-h-[56px] text-[12px] lg:text-[14px] font-normal'
                                                : 'px-[6px] md:px-[11px] lg:px-[18px] py-[3px] md:py-[8px] lg:py-4 min-h-[32px] md:min-h-[40px] lg:min-h-[56px]',
                                        )}
                                    >
                                        {pathname === '/' && (
                                            <motion.span
                                                layoutId="navActiveBg"
                                                className="absolute inset-0 bg-[linear-gradient(180deg,_#3C1C63_0%,_rgba(60,_28,_99,_0)_100%)] rounded-[13px] lg:rounded-[15px]"
                                                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                            />
                                        )}
                                        <span className="relative z-10">
                                            {isNearTop ? (
                                                t("home")
                                            ) : (
                                                <img
                                                    src="/assets/brand/logo.svg"
                                                    alt="Flyingcode logo"
                                                    aria-hidden="true"
                                                    width={80}
                                                    height={80}
                                                    className='h-[20px] md:h-[22px] lg:h-[26px] w-auto object-contain'
                                                />
                                            )}
                                        </span>
                                    </Link>
                                 </li>

                                {navigationItems.map((item) => {
                                    const isActive = pathname === item.href;
                                    return (
                                        <li key={item.href} className="relative">
                                            <Link
                                                href={item.href}
                                                className={cn(
                                                    'relative text-white duration-300 transition-colors px-[11px] lg:px-[25px] py-[8px] lg:py-4 flex items-center min-h-[40px] lg:min-h-[56px] text-[12px] lg:text-[14px] font-normal rounded-[13px] lg:rounded-[15px] whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-theme/50',
                                                    !isActive && 'hover:text-white/50'
                                                )}
                                            >
                                                {isActive && (
                                                    <motion.span
                                                        layoutId="navActiveBg"
                                                        className="absolute inset-0 bg-[linear-gradient(180deg,_#3C1C63_0%,_rgba(60,_28,_99,_0)_100%)] rounded-[13px] lg:rounded-[15px]"
                                                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                                    />
                                                )}
                                                <span className="relative z-10">{item.label}</span>
                                            </Link>
                                        </li>
                                    )
                                })}

                                {/* Language switcher — inside nav pill */}
                                <li className={cn(
                                    'flex items-center transition-all duration-300',
                                    isNearTop ? 'ml-[6px] lg:ml-[14px] mr-[6px] lg:mr-[16px]' : 'ml-[4px] lg:ml-[8px] mr-[1px] lg:mr-[4px]'
                                )}>
                                    <LanguageSwitcher />
                                </li>
                            </ul>
                            </LayoutGroup>
                        </nav>

                        {/* Right - Actions */}
                        <div className='flex items-center gap-4 md:gap-0'>
                            <Button
                                variant='secondary'
                                className='hidden md:inline-flex lg:hidden cursor-pointer w-[40px] h-[40px] min-w-0 rounded-[14px] p-0 items-center justify-center'
                                size='default'
                                onClick={() => setIsGetInTouchOpen(true)}
                                aria-label={t('getInTouch')}
                            >
                                <Icons.email className='w-[17px] h-[17px]' />
                            </Button>

                            <Button
                                variant='secondary'
                                className='cursor-pointer px-[30px] py-[15px] hidden lg:inline-flex'
                                size='default'
                                onClick={() => setIsGetInTouchOpen(true)}
                            >
                                {t('getInTouch')}
                            </Button>

                            {/* Mobile Hamburger Menu */}
                            <motion.button
                                onClick={toggleMobileMenu}
                                className='md:hidden flex flex-col justify-center items-center w-[40px] h-[40px] text-white -translate-y-[2px]'
                                aria-label={tA11y('toggleMobileMenu')}
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                            >
                                <motion.span
                                    className='block rounded-full w-[24px] h-[2px] bg-white self-end'
                                    initial={{ rotate: 0, y: 0, opacity: 1 }}
                                    animate={isMobileMenuOpen ? { rotate: 45, y: 6, width: '24px' } : { rotate: 0, y: 0, width: '24px' }}
                                    transition={{ duration: 0.3 }}
                                />
                                <motion.span
                                    className='block rounded-full w-[24px] h-[2px] bg-white mt-[4px] self-end'
                                    initial={{ opacity: 1, x: 0 }}
                                    animate={isMobileMenuOpen ? { opacity: 0, x: 6 } : { opacity: 1, x: 0, width: '24px' }}
                                    transition={{ duration: 0.2 }}
                                />
                                <motion.span
                                    className='block rounded-full w-[16px] h-[2px] bg-white mt-[4px] self-end'
                                    initial={{ opacity: 1, x: 0 }}
                                    animate={isMobileMenuOpen ? { opacity: 0, x: -6 } : { opacity: 1, x: 0, width: '16px' }}
                                    transition={{ duration: 0.2 }}
                                />
                                <motion.span
                                    className='block rounded-full w-[12px] h-[2px] bg-white mt-[4px] self-end'
                                    initial={{ rotate: 0, y: 0, opacity: 1 }}
                                    animate={isMobileMenuOpen ? { rotate: -45, y: -6, width: '24px' } : { rotate: 0, y: 0, width: '12px' }}
                                    transition={{ duration: 0.3 }}
                                />
                            </motion.button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Menu */}
            <MobileMenu 
                isOpen={isMobileMenuOpen}
                onClose={handleCloseMobileMenu}
                navigationItems={navigationItems}
            />

            {/* Get In Touch Modal */}
            <GetInTouch 
                open={isGetInTouchOpen} 
                onOpenChange={setIsGetInTouchOpen} 
            />
        </>
    );
};

export default Header;